import { MoreDotsIcon } from '@tableau/icons/MoreDotsIcon';
import { FlyoutListMenuWidget, MenuButtonWidget, MenuItemInfo } from '@tableau/widgets';
import React from 'react';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import { style } from 'typestyle';

import { AppState } from '~src/bootstrap/redux/state/AppState';
import { Messages } from '~src/codegen/Localize';
import { ToasterService, ToastType } from '~src/commonInfra/services/ToasterService';
import { MapDispatchToProps, MapStateToProps } from '~src/commonInfra/utils/ConnectUtils';
import { ExtractEncryptionMode } from '~src/commonInfra/utils/sites/SitePropertyTypes';
import { IGetSiteEncryptionKeyResult, IGetSiteEncryptionKeyRootKey } from '~src/settings/security-settings/sections/extract-encryption/models/ExtractEncryptionModels';
import { ISiteAdminSettings, ISiteSettingsForSiteAdmin } from '~src/settings/types/ISiteSettingsForSiteAdmin';

import { getExtractEncryptionKeyRequest } from '../actions/GetExtractEncryptionKeyRequestAction';
import { openKMSKeyActivatePolicyDialog } from '../dialogs/KMSKeyActivateDialog';
import { openKMSKeyDeactivatePolicyDialog } from '../dialogs/KMSKeyDeactivateDialog';
import { openKMSKeyHistoryDialog } from '../dialogs/KMSKeyHistoryDialog';
import { openKMSKeyPolicyDialog } from '../dialogs/KMSKeyPolicyDialog';
import { openKMSKeyRestorePolicyDialog } from '../dialogs/KMSKeyRestoreDialog';
import { openKMSTestConnectionDialog } from '../dialogs/KMSKeyTestConnectionDialog';
import { ActivateSiteEncryptionKeyResult } from '../serverApi/requests/ActivateSiteEncryptionKeyRequest';
import { ActivateSiteEncryptionKeyRequest } from '../serverApi/requests/ActivateSiteEncryptionKeyRequest';
import { activateSiteEncryptionKey } from '../services/ExtractEncryptionKeyService';
import { updateSiteSettings } from '../services/UpdateSiteSettingsService';
import { getSetEncryptionModeEnforcedRequest } from '../utils/ExtractEncrpytionUtils';


export interface KMSEncryptionKeyActionMenuState {
    isOpen: boolean;
}

export interface KMSEncryptionKeyActionMenuOwnProps {
    kmsEncrpytionKey: IGetSiteEncryptionKeyRootKey;
    siteSettings: ISiteSettingsForSiteAdmin;
}

export interface KMSEncryptionKeyActionMenuStateProps {
    encryptionKey: IGetSiteEncryptionKeyResult;
}

export interface KMSEncryptionKeyActionMenuDispatchProps {
    retrieveExtractEncryptionKey: () => void;
}

export type KMSEncryptionKeyActionMenuProps = KMSEncryptionKeyActionMenuOwnProps & KMSEncryptionKeyActionMenuDispatchProps & KMSEncryptionKeyActionMenuStateProps;


const exploreActionsMenuId = 'actions-menu-explorer-id';

const menuButtonDropdowStyle = style(
    { $nest: { '&&::after': { display: 'none' }, '&:disabled' : {borderColor: 'transparent !important'} } }
);

export const menuButtonStyle: React.CSSProperties = {
    borderColor: 'transparent', padding: 0, minWidth: 30
};

export class KMSEncryptionKeyActionMenuComponent extends React.Component<KMSEncryptionKeyActionMenuProps, KMSEncryptionKeyActionMenuState> {
    private buttonElement: HTMLElement | null = null;

    public constructor(props: KMSEncryptionKeyActionMenuProps) {
        super(props);

        this.state = {
            isOpen: false,
        };
    }

    private setButtonRef = (ref: HTMLButtonElement | null): void => {
        this.buttonElement = ref;
    };

    private onSelect(label: string) {
        switch (label) {
            case Messages.extractEncryptionKMS_KeyPolicy_button():
                openKMSKeyPolicyDialog(this.props.kmsEncrpytionKey.externalKeyId, false);
              break;
            case Messages.extractEncryptionKMS_KeyHistory_button():
                openKMSKeyHistoryDialog(this.props.kmsEncrpytionKey.externalKeyId);
                break;
            case Messages.extractEncryptionKMS_TestConnection_button():
                openKMSTestConnectionDialog(this.props.kmsEncrpytionKey.externalKeyId);
                break;
            case Messages.extractEncrpytionKMS_DeactivateKey_button():
                openKMSKeyDeactivatePolicyDialog(this.props.kmsEncrpytionKey.externalKeyId);
                break;
            case Messages.extractEncrpytionKMS_ActivateKey_button():
                if(this.props.encryptionKey?.rootKeys?.some(key => key.keyStatus === 'ACTIVE')) {
                    openKMSKeyActivatePolicyDialog(this.props.kmsEncrpytionKey.externalKeyId, this.props.siteSettings);
                }else{
                    this.activateKey();
                }
                break;
            case Messages.extractEncrpytionKMS_RestoreKey_button():
                openKMSKeyRestorePolicyDialog(this.props.kmsEncrpytionKey.externalKeyId, this.props.siteSettings);
                break;
          }
          this.setState({isOpen: false});
    }

    public render(): JSX.Element {
        return <>
            <MenuButtonWidget
                aria-controls={exploreActionsMenuId}
                isOpen={this.state.isOpen}
                onClick={() => this.setState({ isOpen: !this.state.isOpen })}
                ref={this.setButtonRef}
                testId='kms-encryption-key-actions-menu'
                density='low'
                className={menuButtonDropdowStyle}
                style={menuButtonStyle}
            >
                <MoreDotsIcon size={14}/>
            </MenuButtonWidget>
            {this.state.isOpen && <FlyoutListMenuWidget
                role='menu'
                anchorEl={this.buttonElement}
                allowAutoPosition
                id={exploreActionsMenuId}
                menuItemInfos={this.getMenuInfoItems()}
                onSelect= {(value: string) => this.onSelect(value)}
                requestCloseAll={() => this.setState({ isOpen: false })}
                testId='kms-encryption-key-actions-menu-list'
                // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                toggleEl={this.buttonElement!}
            />}
        </>;
    }

    private getMenuInfoItems(): MenuItemInfo[] {
        return [
            { displayValue: Messages.extractEncryptionKMS_KeyPolicy_button(), value: Messages.extractEncryptionKMS_KeyPolicy_button() },
            { displayValue: Messages.extractEncryptionKMS_KeyHistory_button(), value: Messages.extractEncryptionKMS_KeyHistory_button() },
            { displayValue: `${Messages.extractEncryptionKMS_TestConnection_button()}...`, value: Messages.extractEncryptionKMS_TestConnection_button() },
            { isDivider: true },
            this.getActionButton(this.props.kmsEncrpytionKey.keyStatus)
        ];
    }

    private getActionButton(keyStatus: string): MenuItemInfo {
        switch(keyStatus) {
            case 'ACTIVE':
                return { displayValue: `${Messages.extractEncrpytionKMS_DeactivateKey_button()}...`, value: Messages.extractEncrpytionKMS_DeactivateKey_button(), disabled: true };
            case 'ARCHIVED':
                return { displayValue: `${Messages.extractEncrpytionKMS_DeactivateKey_button()}...`, value: Messages.extractEncrpytionKMS_DeactivateKey_button(), disabled: false };
            case 'PENDING':
                return { displayValue: `${Messages.extractEncrpytionKMS_ActivateKey_button()}...`, value: Messages.extractEncrpytionKMS_ActivateKey_button(), disabled: false };
            case 'DEACTIVATED':
                return { displayValue: `${Messages.extractEncrpytionKMS_RestoreKey_button()}...`, value: Messages.extractEncrpytionKMS_RestoreKey_button(), disabled: false };
            default:
                return { displayValue: '', value: '' };
        }
    }

    private activateKey() {
        activateSiteEncryptionKey(new ActivateSiteEncryptionKeyRequest({externalKeyId: this.props.kmsEncrpytionKey.externalKeyId}))
        .then((response: ActivateSiteEncryptionKeyResult) => {
            if(response.errors){
                ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryption_KMSKeyActivateDialog_FailureToast() });
            } else {
                ToasterService.instance.pop({ type: ToastType.Success, text: Messages.extractEncryption_KMSKeyActivateDialog_SuccessToast() });
                this.updateSiteSettings();
                this.props.retrieveExtractEncryptionKey();
            }
        })
        .catch(() => {
            ToasterService.instance.pop({ type: ToastType.Error, text: Messages.extractEncryption_KMSKeyActivateDialog_FailureToast() });
        }).finally(() => {
            this.setState({isOpen: false});
        });
    }

    private updateSiteSettings() {
        if((this.props.siteSettings?.siteAdminSettings as ISiteAdminSettings)?.extractEncryptionMode !== ExtractEncryptionMode.ENFORCED) {
            updateSiteSettings(getSetEncryptionModeEnforcedRequest(this.props.siteSettings));
        }
    }
}

const mapDispatchToProps: MapDispatchToProps<KMSEncryptionKeyActionMenuDispatchProps> =
  (dispatch) => bindActionCreators({
    retrieveExtractEncryptionKey: getExtractEncryptionKeyRequest
  },
  dispatch);

const mapStateToProps: MapStateToProps<KMSEncryptionKeyActionMenuStateProps> = (state: AppState) => ({
    encryptionKey: state.extractEncryptionInfo.encryptionKey
});

export const KMSEncryptionKeyActionMenu: React.ComponentClass<KMSEncryptionKeyActionMenuOwnProps> =
  connect(mapStateToProps, mapDispatchToProps)(KMSEncryptionKeyActionMenuComponent);